import React from "react";
import { Form, InputGroup, Table } from "react-bootstrap";
import { getBillTaxBreakdownRows } from "../../../../utils/financeEngine";
import "./CartSummary.scss";

const isGstTaxMode = (posTaxType) => String(posTaxType || "").toLowerCase() === "gst";

export const CartSummary = ({
    t,
    discountType,
    onDiscountTypeChange,
    discountValue,
    onDiscountValueChange,
    transportCost,
    onTransportCostChange,
    taxes,
    totals,
    getCurrencySymbol,
    disableScrollProps,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    cart,
    itemTaxes,
    scrollHeight,
    showPosTax = true,
}) => {
    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;

    const taxBreakdown = !cart || !itemTaxes
        ? []
        : getBillTaxBreakdownRows(
            cart,
            itemTaxes,
            totals,
            posTaxType,
            isSameState,
            "sale",
            discountType,
            discountValue
        );

    const totalTaxableBeforeDiscountFromBreakdown = taxBreakdown.reduce(
        (sum, g) => sum + (g.taxableAmountBeforeDiscount || 0),
        0
    );
    const netTaxableFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.netTaxableAmount, 0);
    const totalCGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.CGST, 0);
    const totalSGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.SGST, 0);
    const totalIGSTFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.IGST, 0);
    const totalVATFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.VAT, 0);
    const totalTaxFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalTax, 0);
    const totalAmountFromBreakdown = taxBreakdown.reduce((sum, g) => sum + g.totalAmount, 0);

    const hasTaxRatesInCart = showPosTax && cart.some((item) => Number(item.taxPercentage) > 0);
    const hasTax =
        showPosTax &&
        cart.length > 0 &&
        (totals.tax > 0 || hasTaxRatesInCart) &&
        (taxBreakdown.length > 0 || hasTaxRatesInCart);
    const showSimpleSummary = cart.length > 0 && !hasTax;

    const renderDiscountField = () => (
        <>
            <Form.Label className="mb-0 text-nowrap pos-cart-summary__discount-label">
                {t("Discount")} {discountType === "Percentage" && "(%)"}
            </Form.Label>

            {discountType === "Percentage" ? (
                <InputGroup size="sm" className="pos-cart-summary__discount-input">
                    <Form.Control
                        type="number"
                        value={discountValue}
                        min={0}
                        max={100}
                        onChange={(e) =>
                            onDiscountValueChange(
                                e.target.value > 0 && e.target.value <= 100 ? e.target.value : 0
                            )
                        }
                        {...disableScrollProps}
                    />
                    <InputGroup.Text>
                        {getCurrencySymbol()}
                        {totals.discount.toFixed(2)}
                    </InputGroup.Text>
                </InputGroup>
            ) : (
                <Form.Control
                    type="number"
                    size="sm"
                    className="pos-cart-summary__discount-input"
                    min={0}
                    value={discountValue}
                    onChange={(e) =>
                        onDiscountValueChange(
                            e.target.value > 0 && e.target.value <= totals.netTotal
                                ? e.target.value
                                : 0
                        )
                    }
                    {...disableScrollProps}
                />
            )}
        </>
    );

    const renderTotalsTable = ({ useTaxLabels = true } = {}) => (
        <Table bordered size="sm" className="pos-cart-summary__totals-table mb-0">
            <tbody>
                <tr>
                    <td className="text-end">
                        {useTaxLabels ? t("Total Taxable Value") : t("Subtotal")}
                    </td>
                    <td className="text-end fw-bold">
                        {getCurrencySymbol()}
                        {(useTaxLabels
                            ? totalTaxableBeforeDiscountFromBreakdown
                            : totals.subtotal
                        ).toFixed(2)}
                    </td>
                </tr>
                {totals.discount > 0 && (
                    <>
                        <tr>
                            <td className="text-end">{t("Discount")}</td>
                            <td className="text-end text-danger">
                                -{getCurrencySymbol()}{totals.discount.toFixed(2)}
                            </td>
                        </tr>
                        {useTaxLabels ? (
                            <tr>
                                <td className="text-end">{t("Net Taxable Value")}</td>
                                <td className="text-end fw-bold">
                                    {getCurrencySymbol()}{netTaxableFromBreakdown.toFixed(2)}
                                </td>
                            </tr>
                        ) : (
                            <tr>
                                <td className="text-end">{t("Net Amount")}</td>
                                <td className="text-end fw-bold">
                                    {getCurrencySymbol()}{(totals.subtotal - totals.discount).toFixed(2)}
                                </td>
                            </tr>
                        )}
                    </>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && isSameState && (totalCGSTFromBreakdown > 0 || totals.CGST > 0) && (
                    <tr>
                        <td className="text-end">Add: CGST</td>
                        <td className="text-end text-success">
                            +{getCurrencySymbol()}{(totalCGSTFromBreakdown || totals.CGST || 0).toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && isSameState && (totalSGSTFromBreakdown > 0 || totals.SGST > 0) && (
                    <tr>
                        <td className="text-end">Add: SGST</td>
                        <td className="text-end text-success">
                            +{getCurrencySymbol()}{(totalSGSTFromBreakdown || totals.SGST || 0).toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && isGstTaxMode(posTaxType) && !isSameState && (totalIGSTFromBreakdown > 0 || totals.IGST > 0) && (
                    <tr>
                        <td className="text-end">Add: IGST</td>
                        <td className="text-end text-success">
                            +{getCurrencySymbol()}{(totalIGSTFromBreakdown || totals.IGST || 0).toFixed(2)}
                        </td>
                    </tr>
                )}
                {useTaxLabels && !isGstTaxMode(posTaxType) && (totalVATFromBreakdown > 0 || totals.VAT > 0 || totals.tax > 0) && (
                    <tr>
                        <td className="text-end">Add: VAT</td>
                        <td className="text-end text-success">
                            +{getCurrencySymbol()}{(totalVATFromBreakdown || totals.VAT || totals.tax || 0).toFixed(2)}
                        </td>
                    </tr>
                )}
                {!useTaxLabels && totals.transport > 0 && (
                    <tr>
                        <td className="text-end">{t("Transport")}</td>
                        <td className="text-end">
                            +{getCurrencySymbol()}{totals.transport.toFixed(2)}
                        </td>
                    </tr>
                )}
                <tr className="table-primary">
                    <td className="text-end fw-bold fs-6">{t("Grand Total")}</td>
                    <td className="text-end fw-bold fs-6 text-primary">
                        {getCurrencySymbol()}{totals.netTotal.toFixed(2)}
                    </td>
                </tr>
            </tbody>
        </Table>
    );

    return (
        <div className={`pos-cart-summary${showSimpleSummary ? " pos-cart-summary--compact" : ""}`}>
            <div className="pos-cart-summary__discount">
                {renderDiscountField()}
                {/* show grand total  for no tax */}
                {showSimpleSummary && (
                    //   simple grand total for in
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        <h6 className="pos-cart-summary__heading mb-0">{t("Grand Total")}</h6>
                        <span className="text-end fw-bold fs-6 text-primary">
                            {getCurrencySymbol()}{totals.netTotal.toFixed(2)}
                        </span>
                    </div>
                )}
            </div>

            <div
                className="pos-cart-summary__scroll"
                style={
                    scrollHeight
                        ? { height: scrollHeight, maxHeight: scrollHeight, minHeight: scrollHeight }
                        : undefined
                }
            >
                {cart.length === 0 && (
                    <p className="pos-cart-summary__empty">
                        {t("Add items to view summary and totals")}
                    </p>
                )}

                {showSimpleSummary && (
                    <div className="pos-cart-summary__simple-table">
                        <h6 className="pos-cart-summary__heading">{t("Bill Summary")}</h6>
                        {renderTotalsTable({ useTaxLabels: false })}
                    </div>
                )}

                {hasTax && (
                    <div className="pos-cart-summary__row">
                        <div className="pos-cart-summary__tax-col">
                            <h6 className="pos-cart-summary__heading">{t("Tax Summary")}</h6>
                            <Table bordered size="sm" className="pos-cart-summary__tax-table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="text-center">{t("Tax %")}</th>
                                        <th className="text-end">{t("Taxable value")}</th>
                                        {totals.discount > 0 && (
                                            <th className="text-end">{t("Taxable Amount (Discounted)")}</th>
                                        )}
                                        {isGstTaxMode(posTaxType) ? (
                                            isSameState ? (
                                                <>
                                                    <th className="text-end">{t("CGST Amt")}</th>
                                                    <th className="text-end">{t("SGST Amt")}</th>
                                                </>
                                            ) : (
                                                <th className="text-end">{t("IGST Amt")}</th>
                                            )
                                        ) : (
                                            <th className="text-end">{t("VAT Amt")}</th>
                                        )}
                                        <th className="text-end">{t("Total Tax")}</th>
                                        <th className="text-end">{t("Total Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {taxBreakdown.map((group, index) => (
                                        group.taxRate > 0 && (
                                            <tr key={index}>
                                                <td className="text-center fw-bold">{group.taxRate}%</td>
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{group.taxableAmountBeforeDiscount.toFixed(2)}
                                                </td>
                                                {totals.discount > 0 && (
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{group.taxableAmountAfterDiscount.toFixed(2)}
                                                    </td>
                                                )}
                                                {isGstTaxMode(posTaxType) ? (
                                                    isSameState ? (
                                                        <>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.CGST.toFixed(2)}
                                                            </td>
                                                            <td className="text-end text-success">
                                                                {getCurrencySymbol()}{group.SGST.toFixed(2)}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <td className="text-end text-success">
                                                            {getCurrencySymbol()}{group.IGST.toFixed(2)}
                                                        </td>
                                                    )
                                                ) : (
                                                    <td className="text-end text-success">
                                                        {getCurrencySymbol()}{group.VAT.toFixed(2)}
                                                    </td>
                                                )}
                                                <td className="text-end fw-bold text-success">
                                                    {getCurrencySymbol()}{group.totalTax.toFixed(2)}
                                                </td>
                                                <td className="text-end fw-bold">
                                                    {getCurrencySymbol()}{group.totalAmount.toFixed(2)}
                                                </td>
                                            </tr>
                                        )
                                    ))}
                                    <tr className="table-primary fw-bold">
                                        <td className="text-center">{t("Total")}</td>
                                        <td className="text-end">
                                            {getCurrencySymbol()}{totalTaxableBeforeDiscountFromBreakdown.toFixed(2)}
                                        </td>
                                        {totals.discount > 0 && (
                                            <td className="text-end">
                                                {getCurrencySymbol()}{netTaxableFromBreakdown.toFixed(2)}
                                            </td>
                                        )}
                                        {isGstTaxMode(posTaxType) ? (
                                            isSameState ? (
                                                <>
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalCGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                    <td className="text-end">
                                                        {getCurrencySymbol()}{totalSGSTFromBreakdown.toFixed(2)}
                                                    </td>
                                                </>
                                            ) : (
                                                <td className="text-end">
                                                    {getCurrencySymbol()}{totalIGSTFromBreakdown.toFixed(2)}
                                                </td>
                                            )
                                        ) : (
                                            <td className="text-end">
                                                {getCurrencySymbol()}{totalVATFromBreakdown.toFixed(2)}
                                            </td>
                                        )}
                                        <td className="text-end text-success">
                                            {getCurrencySymbol()}{totalTaxFromBreakdown.toFixed(2)}
                                        </td>
                                        <td className="text-end">
                                            {getCurrencySymbol()}{totalAmountFromBreakdown.toFixed(2)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>

                        <div className="pos-cart-summary__totals-col">
                            {renderTotalsTable({ useTaxLabels: true })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartSummary;
